import React, { useEffect, useRef, useState } from 'react';
import { setContactRole } from '../lib/leads';

// "Your journey": the whole MbuziPlus product as one winding line, left to right, in eight steps.
// Steps 1 to 3 are free, steps 4 to 6 are paid, steps 7 and 8 are included and ongoing.
// Every step is a button that opens the right place. Used on the MbuziPlus page through the "journey" slot.
const WA = 'https://wa.me/254110563185?text=';
const wa = (t) => WA + encodeURIComponent(t);
const put = (k, v) => { try { sessionStorage.setItem(k, v); } catch (e) { /* ignore */ } };
const open = (u) => window.open(u, '_blank', 'noopener,noreferrer');

const GREEN = '#3C7F30', ORANGE = '#D55103', DARK = '#14301A';
const XS = [0, 1, 2, 3, 4, 5, 6, 7].map((i) => (i + 0.5) * 150);
const YS = XS.map((_, i) => (i % 2 === 0 ? 300 : 470));
const PATH = XS.reduce((d, x, i) => {
  if (i === 0) return `M ${x} ${YS[0]}`;
  const cx = (XS[i - 1] + x) / 2;
  return `${d} C ${cx} ${YS[i - 1]}, ${cx} ${YS[i]}, ${x} ${YS[i]}`;
}, '');

export default function JourneyFlow({ onNavigate, onRegister }) {
  const ref = useRef(null);
  const [seen, setSeen] = useState(() => typeof IntersectionObserver === 'undefined');
  const vref = useRef(null);

  // Phones: play the journey video while it is on screen, pause it when it is not (never autoplay for people who prefer less motion).
  useEffect(() => {
    const v = vref.current;
    if (!v || typeof IntersectionObserver === 'undefined') return undefined;
    const calm = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (calm) return undefined;
    const io = new IntersectionObserver((es) => {
      es.forEach((e) => {
        if (e.isIntersecting) { const pr = v.play(); if (pr && pr.catch) pr.catch(() => {}); } else { v.pause(); }
      });
    }, { threshold: 0.6 });
    io.observe(v);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (seen || !ref.current) return undefined;
    const io = new IntersectionObserver((es) => { if (es.some((e) => e.isIntersecting)) { setSeen(true); io.disconnect(); } }, { threshold: 0.1 });
    io.observe(ref.current);
    return () => io.disconnect();
  }, [seen]);

  const STEPS = [
    { e: '👀', t: 'See', s: 'Tap the link you receive', tag: 'free', go: () => open(wa('Hello! Imfuyo has free lessons, insurance and loans for livestock farmers: https://www.imfuyo.farm/apply')) },
    { e: '▶️', t: 'Try', s: 'One free lesson, 3 minutes', tag: 'free', go: () => { put('imfuyo-scroll-to', 'free-preview'); onNavigate && onNavigate('academy'); } },
    { e: '✍️', t: 'Sign up', s: 'Your name, phone and county', tag: 'free', go: () => { setContactRole('farmers'); onNavigate && onNavigate('landing', 'contact'); } },
    { e: '🎓', t: 'Learn', s: 'MbuziPlus Course and certificate', price: 'Kenya Shillings 4,900', tag: 'pay', go: () => onRegister && onRegister() },
    { e: '🏡', t: 'Visit', s: 'A real goat farm, with your group', price: 'From Kenya Shillings 1,500', tag: 'pay', go: () => open(wa('Hello Imfuyo, our group would like to apply for an MbuziPlus farm visit.')) },
    { e: '🐐', t: 'Get goats', s: 'Two goats as productive assets, with feed and insurance', price: 'Kenya Shillings 67,000', tag: 'pay', go: () => open(wa('Hello Imfuyo, I would like to know more about the MbuziPlus Package.')) },
    { e: '🛡️', t: 'Be safe', s: 'Ear tag and insurance', tag: 'inc', go: () => onNavigate && onNavigate('insurance') },
    { e: '🌱', t: 'Grow', s: 'Sell milk and goats, ask for a loan', tag: 'inc', go: () => onNavigate && onNavigate('loans') },
  ];
  const color = (tag) => (tag === 'free' ? GREEN : tag === 'pay' ? ORANGE : DARK);

  return (
    <div ref={ref} className={`jf ${seen ? 'jf-in' : ''}`}>
      <style>{CSS}</style>

      {/* Wide screens: one winding line, left to right */}
      <div className="jf-wide" aria-hidden={false}>
        <div className="jf-stage">
          <svg className="jf-svg" viewBox="0 0 1200 760" preserveAspectRatio="none" aria-hidden="true">
            <path d={PATH} fill="none" stroke="#DCE9D3" strokeWidth="30" strokeLinecap="round" />
            <path d={PATH} fill="none" stroke="#8DB87A" strokeWidth="5" strokeDasharray="2 20" strokeLinecap="round" className="jf-dash" />
          </svg>
          <span className="jf-end" aria-hidden="true">&#9654;</span>
          <ol className="jf-list">
            {STEPS.map((st, i) => {
              const up = i % 2 === 0;
              return (
                <li key={st.t} className="jf-node" style={{ left: `${(XS[i] / 1200) * 100}%`, top: `${(YS[i] / 760) * 100}%`, '--c': color(st.tag), '--d': `${i * 110}ms` }}>
                  <button type="button" onClick={st.go} className={`jf-btn ${up ? 'jf-up' : 'jf-down'}`} aria-label={`Step ${i + 1}: ${st.t}. ${st.s}`}>
                    <span className="jf-disc"><span className="jf-emo" aria-hidden="true">{st.e}</span><span className="jf-num">{i + 1}</span></span>
                    <span className="jf-txt"><b>{st.t}</b><span>{st.s}</span>{st.price && <em>{st.price}</em>}</span>
                  </button>
                </li>
              );
            })}
          </ol>
          <div className="jf-strip">
            <span style={{ width: '37.5%', background: GREEN }}>FREE <i>Steps 1 to 3</i></span>
            <span style={{ width: '37.5%', background: ORANGE }}>YOU PAY <i>Steps 4 to 6</i></span>
            <span style={{ width: '25%', background: DARK }}>INCLUDED <i>Steps 7 and 8</i></span>
          </div>
        </div>
      </div>

      {/* Phones and small screens: the journey animation, with the two main actions below it */}
      <div className="jf-tall">
        <video ref={vref} className="jf-video" src="/media/mbuziplus-journey.mp4" poster="/media/mbuziplus-journey-poster.jpg" muted loop playsInline controls preload="metadata" aria-label="Animation of the MbuziPlus journey in eight steps: See, Try, Sign up, Learn, Visit, Get goats, Be safe and Grow." />
        <p className="jf-vcap">Watch the journey in 16 seconds. Tap the picture to pause, or the corner to make it bigger.</p>
        <button type="button" className="jf-main" onClick={STEPS[1].go}>Try a free lesson <span aria-hidden="true">&rarr;</span></button>
        <button type="button" className="jf-second" onClick={STEPS[3].go}>Register and pay <span aria-hidden="true">&rarr;</span></button>
      </div>
      <p className="jf-hint">Tap a step to begin.</p>
    </div>
  );
}

const CSS = `
.jf{--ff:Inter,system-ui,sans-serif;font-family:var(--ff);color:#14301A}
.jf button{font-family:var(--ff);cursor:pointer;border:0;background:none;padding:0;color:inherit;text-align:center}
.jf-stage{position:relative;width:100%;aspect-ratio:1200/760;container-type:inline-size}
.jf-svg{position:absolute;inset:0;width:100%;height:100%}
.jf-end{position:absolute;right:.5%;top:61.8%;transform:translateY(-50%);font-size:2.6cqw;color:#14301A}
.jf-list{list-style:none;margin:0;padding:0}
.jf-node{position:absolute;width:0;height:0;opacity:0;transition:opacity .6s ease var(--d)}
.jf-in .jf-node{opacity:1}
.jf-btn{position:absolute;left:0;top:0;transform:translateX(-50%);width:15.6cqw;display:flex;align-items:center;gap:1.2cqw}
.jf-up{flex-direction:column-reverse;bottom:0;top:auto;transform:translate(-50%,0);margin-bottom:-9.2cqw}
.jf-down{flex-direction:column;transform:translate(-50%,-9.2cqw)}
.jf-disc{position:relative;flex:none;width:9cqw;height:9cqw;border-radius:50%;background:#fff;border:.65cqw solid var(--c);display:flex;align-items:center;justify-content:center;box-shadow:0 .5cqw 1.5cqw rgba(20,48,26,.22);transition:transform .2s}
.jf-btn:hover .jf-disc,.jf-btn:focus-visible .jf-disc,.jf-row:hover .jf-disc{transform:scale(1.08)}
.jf-btn:focus-visible,.jf-row:focus-visible{outline:3px solid #B7D9A8;outline-offset:6px;border-radius:12px}
.jf-emo{font-size:4.2cqw;line-height:1}
.jf-num{position:absolute;right:-.4cqw;top:-.4cqw;width:2.8cqw;height:2.8cqw;border-radius:50%;background:var(--c);color:#fff;font:800 1.5cqw/2.8cqw var(--ff);text-align:center;border:.25cqw solid #fff}
.jf-txt{display:flex;flex-direction:column;align-items:center;gap:.3cqw}
.jf-up .jf-txt{margin-bottom:1.1cqw}
.jf-down .jf-txt{margin-top:1.1cqw}
.jf-txt b{font:800 2.5cqw/1.05 var(--ff);color:#14301A}
.jf-txt span{font:500 1.35cqw/1.3 var(--ff);color:#44574a}
.jf-txt em{font:800 1.3cqw/1.2 var(--ff);font-style:normal;color:#BF4700}
.jf-strip{position:absolute;left:0;right:0;bottom:0;display:flex;height:7%;border-radius:999px;overflow:hidden;box-shadow:0 .4cqw 1.2cqw rgba(20,48,26,.15)}
.jf-strip span{display:flex;align-items:center;justify-content:center;gap:1cqw;color:#fff;font:800 1.5cqw var(--ff);letter-spacing:.25cqw}
.jf-strip i{font:500 1.3cqw var(--ff);font-style:normal;letter-spacing:0;opacity:.9}
.jf-dash{animation:jfmove 14s linear infinite}
@keyframes jfmove{to{stroke-dashoffset:-220}}
.jf-tall{display:none}
.jf-hint{margin:26px 0 0;font:600 16px var(--ff);color:#44574a;text-align:center}
@media (max-width:820px){
  .jf-wide,.jf-hint{display:none}
  .jf-tall{display:block}
  .jf-video{display:block;width:100%;aspect-ratio:16/9;border-radius:18px;background:#14301A;box-shadow:0 8px 24px rgba(20,48,26,.2)}
  .jf-vcap{margin:12px 4px 18px;font:500 15px/1.35 var(--ff);color:#44574a;text-align:center}
  .jf .jf-main{display:block;width:100%;margin:0 0 12px;padding:16px 20px;border-radius:999px;background:#D55103;color:#fff;font:800 20px var(--ff);box-shadow:0 6px 16px rgba(213,81,3,.35)}
  .jf .jf-second{display:block;width:100%;margin:0;padding:14px 20px;border-radius:999px;border:2px solid #14301A;color:#14301A;font:800 18px var(--ff)}
}
@media (prefers-reduced-motion:reduce){.jf-dash{animation:none}.jf-node{transition:none;opacity:1;transform:none}}
`;
